/* ==========================================================================
   FUEL PDP gallery controller.

   RJ, 2026-09-01: "PDP images not scrollable or swipable need to fix."
   RJ, 2026-09-02: PDP gallery must respect the grain picker (TASK-313) - a
   product carrying both millet and rice photography must never show a mixed,
   unexplained 24-frame set, and the counter must never lie about how many
   frames the current grain actually has.

   THE SWIPE IS NOT IN HERE. Scroll snapping in fuel-gallery.css does that, and
   it does it with the browser's own momentum physics. This file only reports
   which frame is showing and offers three other ways to change it: the thumb
   rail, the arrows, and the left/right keys. If it fails to parse, the gallery
   still scrolls and still snaps; it just stops counting.

   VISIBLE-SET INDEXING
   fuel-gallery.liquid tags every slide, dot and thumb with data-grain, and
   fuel-gallery.css hides the grain not currently selected (only when the
   product actually carries both, see data-fuel-gal-mixed). So "the third
   photograph" can mean a different absolute DOM element depending on which
   grain is active. Every navigation and count in this file works in VISIBLE
   position, computed fresh from which elements are actually laid out
   (offsetParent !== null), and remapped whenever fuel-grain.js reports a
   change via the `fuel:grain` document event it already fires. Absolute
   DOM index (data-fuel-gal-slide) is still what ties a slide to its matching
   thumb/dot - it never changes - but it is not what gets shown or counted.

   No framework, no build step, no dependency. Multiple galleries on one page
   are supported because everything is looked up inside the root element.
   ========================================================================== */
(function () {
  'use strict';

  function setup(root) {
    var track = root.querySelector('[data-fuel-gal-track]');
    if (!track) return;

    var allSlides = Array.prototype.slice.call(
      root.querySelectorAll('[data-fuel-gal-slide]')
    );
    if (allSlides.length < 2) return;

    var allThumbs = Array.prototype.slice.call(root.querySelectorAll('[data-fuel-gal-thumb]'));
    var allDots = Array.prototype.slice.call(root.querySelectorAll('[data-fuel-gal-dot]'));
    var now = root.querySelector('[data-fuel-gal-now]');
    var total = root.querySelector('[data-fuel-gal-total]');
    var prev = root.querySelector('[data-fuel-gal-prev]');
    var next = root.querySelector('[data-fuel-gal-next]');

    var io = null;
    var visible = [];   // [{ slide, thumb, dot, abs }], recomputed on grain change
    var pos = 0;         // index INTO `visible`, never an absolute DOM index

    function isVisible(el) {
      // display:none collapses offsetParent to null. Cheap, no layout read
      // beyond what the browser already keeps, and correct for the
      // ancestor-chain case (a hidden slide inside an always-visible track).
      return !!el && el.offsetParent !== null;
    }

    function computeVisible() {
      visible = [];
      for (var i = 0; i < allSlides.length; i++) {
        if (isVisible(allSlides[i])) {
          visible.push({
            slide: allSlides[i],
            thumb: allThumbs[i] || null,
            dot: allDots[i] || null,
            abs: i
          });
        }
      }
    }

    function paint(newPos) {
      if (!visible.length) return;
      pos = Math.max(0, Math.min(visible.length - 1, newPos));
      var entry = visible[pos];
      if (!entry) return;

      if (now) now.textContent = String(pos + 1);
      if (total) total.textContent = String(visible.length);

      for (var t = 0; t < allThumbs.length; t++) {
        allThumbs[t].removeAttribute('aria-current');
      }
      if (entry.thumb) entry.thumb.setAttribute('aria-current', 'true');

      for (var d = 0; d < allDots.length; d++) {
        allDots[d].removeAttribute('data-active');
      }
      if (entry.dot) entry.dot.setAttribute('data-active', '');

      if (prev) prev.disabled = pos === 0;
      if (next) next.disabled = pos === visible.length - 1;

      // Keep the active thumb in view without dragging the PAGE sideways.
      // scrollIntoView with inline:'nearest' on a thumb inside its own
      // horizontal scroller does exactly that; block:'nearest' stops it
      // yanking the whole document vertically on a phone.
      if (entry.thumb && entry.thumb.scrollIntoView) {
        entry.thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    function goPos(p, smooth) {
      if (!visible.length) return;
      var target = Math.max(0, Math.min(visible.length - 1, p));
      var entry = visible[target];
      if (!entry) return;
      // scrollLeft rather than slide.scrollIntoView: scrollIntoView on a slide
      // also scrolls the PAGE to bring the gallery into view, which on a phone
      // jumps the customer away from the button they just pressed.
      track.scrollTo({
        left: entry.slide.offsetLeft - track.offsetLeft,
        behavior: smooth === false ? 'auto' : 'smooth'
      });
      paint(target);
    }

    /* Which slide is showing is answered by the browser, not by arithmetic on
       scrollLeft. threshold 0.6 means the frame has to be mostly on screen
       before the counter moves, which matches what the eye calls "this one".
       Re-observing on every grain change is what keeps a hidden slide from
       ever reporting an intersection it cannot physically have. */
    function observe() {
      if (io) io.disconnect();
      if (!('IntersectionObserver' in window)) return;
      io = new IntersectionObserver(function (entries) {
        var best = null;
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          if (!best || entries[i].intersectionRatio > best.intersectionRatio) {
            best = entries[i];
          }
        }
        if (!best) return;
        var absIdx = parseInt(best.target.getAttribute('data-fuel-gal-slide'), 10);
        for (var v = 0; v < visible.length; v++) {
          if (visible[v].abs === absIdx) { paint(v); return; }
        }
      }, { root: track, threshold: [0.6] });
      visible.forEach(function (entry) { io.observe(entry.slide); });
    }

    function refresh(resetToStart) {
      computeVisible();
      observe();
      if (!visible.length) return; // structurally unreachable, see fuel-gallery.liquid
      if (resetToStart) {
        pos = 0;
        goPos(0, false);
      } else {
        paint(Math.min(pos, visible.length - 1));
      }
    }

    for (var t2 = 0; t2 < allThumbs.length; t2++) {
      (function (button, abs) {
        button.addEventListener('click', function () {
          for (var v = 0; v < visible.length; v++) {
            if (visible[v].abs === abs) { goPos(v); return; }
          }
        });
      })(allThumbs[t2], t2);
    }

    if (prev) prev.addEventListener('click', function () { goPos(pos - 1); });
    if (next) next.addEventListener('click', function () { goPos(pos + 1); });

    track.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goPos(pos + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPos(pos - 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        goPos(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        goPos(visible.length - 1);
      }
    });

    // fuel-grain.js fires this on document, undelegated, whenever the grain
    // radio changes. Set the attribute fuel-gallery.css keys its hide rules
    // off, then reset to slide 1 and recount from what is actually laid out
    // now - a "3 / 24" surviving into an 11-frame view is worse than no
    // counter at all.
    document.addEventListener('fuel:grain', function (event) {
      var grain = event && event.detail && event.detail.grain;
      if (grain) root.setAttribute('data-fuel-gal-active', grain);
      refresh(true);
    });

    refresh(true);
  }

  function init() {
    var roots = document.querySelectorAll('[data-fuel-gallery]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
